import { shallowEqual } from "react-redux";
import { createSelectorCreator, defaultMemoize } from "reselect";
import { PotentialBet } from "@ppb/bet-engine";
import { getExchangeRunnerTree } from "../exchange-markets/exchange-markets-selectors";
import { type ExchangeMarketsReadState } from "../exchange-markets/ExchangeMarkets.types";
import type { URN } from "../../../types";
import type { ExchangeBettingState } from "./ExchangeBetting.types";

/**
 * Narrowed on purpose, and `exchangeBetting` and `exchangeMarkets` are optional here even though
 * BetslipApplicationState declares them present. ExchangeMarketRunner calls useHasRunnerContext
 * unconditionally, so this selector runs on the host render path in sessions where exc-betting-module
 * is never registered, and during the async register() window for exchange users who switch products.
 */
type RunnerPotentialBetsState = ExchangeMarketsReadState & {
  exchangeBetting?: ExchangeBettingState;
};

type PotentialBetWithPNL = PotentialBet & {
  pnl?: {
    win?: number;
    lose?: number;
  };
};

const arePotentialBetsEqual: (
  potentialBets: PotentialBet[] | undefined,
  updatedPotentialBets: PotentialBet[] | undefined,
) => boolean = (potentialBets, updatedPotentialBets) => {
  if (potentialBets === updatedPotentialBets) {
    return true;
  }
  if (potentialBets === undefined || updatedPotentialBets === undefined) {
    return false;
  }

  if (potentialBets.length !== updatedPotentialBets.length) {
    return false;
  }

  for (let i = 0; i < potentialBets.length; i += 1) {
    const potentialBet: PotentialBetWithPNL = { ...potentialBets[i] };
    const updatedPotentialBet: PotentialBetWithPNL = { ...updatedPotentialBets[i] };

    if (!shallowEqual(potentialBet.pnl, updatedPotentialBet.pnl)) {
      return false;
    }

    delete potentialBet.pnl;
    delete updatedPotentialBet.pnl;
    if (!shallowEqual(potentialBet, updatedPotentialBet)) {
      return false;
    }
  }

  return true;
};

export const createRunnerPotentialBetsSelector = () =>
  createSelectorCreator(defaultMemoize, arePotentialBetsEqual)(
    [
      (state: RunnerPotentialBetsState, urn: URN) => {
        const runnerTree = getExchangeRunnerTree(state, urn);
        if (!runnerTree?.market) {
          return undefined;
        }
        const marketPotentialBets = state.exchangeBetting?.[runnerTree.market.urn]?.potentialBets;

        if (!marketPotentialBets) {
          return undefined;
        }
        return marketPotentialBets.filter(
          (bet: PotentialBet) =>
            bet.selectionId === runnerTree.marketRunner.selectionId &&
            bet.handicap === runnerTree.marketRunner.handicap,
        );
      },
    ],
    (potentialBets) => potentialBets || [],
  );
